import React, { useState, useEffect } from 'react';
import './SalesForecastingDashboard.css';
import { API_ENDPOINTS } from '../../config/apiRouting';

interface SalesHistoryData {
  date: string;
  hour: number;
  sales: number;
  transactions: number;
  customers: number;
  averageTicket: number;
  dayOfWeek: number;
  isHoliday: boolean;
  weatherCondition: string;
  temperature: number;
}

interface SalesForecast {
  forecastId: string;
  storeId: string;
  forecastWeek: string;
  date: string;
  hour: number;
  forecastedSales: number;
  adjustedSales: number;
  confidence: number;
  factors: {
    weather: number;
    holiday: number;
    trend: number;
    competition: number;
  };
  status: 'draft' | 'approved' | 'active';
}

interface ForecastingConfig {
  configId: string;
  switchName: string;
  description: string;
  isEnabled: boolean;
  weight: number;
  defaultValue: number;
  userValue: number;
}

interface ShiftOptimization {
  optimizationId: string;
  storeId: string;
  date: string;
  shiftType: string;
  recommendedStaff: {
    FOH: number;
    BOH: number;
    MANAGEMENT: number;
  };
  requiredSkills: string[];
  estimatedLabor: number;
  laborPercentage: number;
  qualityScore: number;
}

interface SalesForecastingDashboardProps {
  storeId: string;
}

export const SalesForecastingDashboard: React.FC<SalesForecastingDashboardProps> = ({ storeId }) => {
  const [salesHistory, setSalesHistory] = useState<SalesHistoryData[]>([]);
  const [forecasts, setForecasts] = useState<SalesForecast[]>([]);
  const [optimizations, setOptimizations] = useState<ShiftOptimization[]>([]);
  const [forecastingConfig, setForecastingConfig] = useState<ForecastingConfig[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'history' | 'forecast' | 'optimization'>('history');

  useEffect(() => {
    const currentWeek = getCurrentWeek();
    setSelectedWeek(currentWeek);
    fetchSalesHistory();
    fetchForecastingConfig();
  }, [storeId]);

  useEffect(() => {
    if (selectedWeek) {
      fetchForecasts();
      fetchOptimizations();
    }
  }, [selectedWeek, storeId]);

  const getCurrentWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    const week = getWeekNumber(now);
    return `${year}-W${week.toString().padStart(2, '0')}`;
  };

  const getWeekNumber = (date: Date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  const fetchSalesHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.employees()}/sales-history/${storeId}?days=30&groupBy=hour`);
      if (!response.ok) throw new Error('Failed to fetch sales history');
      
      const data = await response.json();
      setSalesHistory(data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sales history');
    } finally {
      setLoading(false);
    }
  };

  const fetchForecasts = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.employees()}/forecast/${storeId}/${selectedWeek}`);
      if (!response.ok) throw new Error('Failed to fetch forecasts');
      
      const data = await response.json();
      setForecasts(data.data || []);
    } catch (err) {
      console.error('Error fetching forecasts:', err);
    }
  };

  const fetchOptimizations = async () => {
    try {
      const weekDates = getWeekDates(selectedWeek);
      const optimizationPromises = weekDates.map(date => 
        fetch(`${API_ENDPOINTS.employees()}/optimization/${storeId}/${date}`)
          .then(res => res.ok ? res.json() : null)
          .then(data => data?.data || [])
      );
      
      const results = await Promise.all(optimizationPromises);
      setOptimizations(results.flat());
    } catch (err) {
      console.error('Error fetching optimizations:', err);
    }
  };

  const fetchForecastingConfig = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.employees()}/forecast-config/${storeId}`);
      if (!response.ok) throw new Error('Failed to fetch forecasting config');
      
      const data = await response.json();
      setForecastingConfig(data.data || []);
    } catch (err) {
      console.error('Error fetching forecasting config:', err);
    }
  };

  const generateAIForecast = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.employees()}/forecast/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId,
          forecastWeek: selectedWeek,
          includeWeather: true,
          includeHolidays: true
        })
      });

      if (!response.ok) throw new Error('Failed to generate forecast');
      
      const result = await response.json();
      setForecasts(result.data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate forecast');
    } finally {
      setLoading(false);
    }
  };

  const generateOptimization = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.employees()}/optimization/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId,
          forecastWeek: selectedWeek,
          optimizationFactors: forecastingConfig.reduce((acc, config) => {
            acc[config.switchName.toLowerCase().replace(/\s+/g, '_')] = config.userValue;
            return acc;
          }, {} as Record<string, number>)
        })
      });

      if (!response.ok) throw new Error('Failed to generate optimization');
      
      const result = await response.json();
      setOptimizations(result.data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate optimization');
    } finally {
      setLoading(false);
    }
  };

  const updateForecast = async (forecastId: string, adjustedSales: number) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.employees()}/forecast/${forecastId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adjustedSales })
      });

      if (!response.ok) throw new Error('Failed to update forecast');
      
      setForecasts(prev => prev.map(f => 
        f.forecastId === forecastId ? { ...f, adjustedSales } : f
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update forecast');
    }
  };

  const updateForecastingSwitch = async (configId: string, userValue: number) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.employees()}/forecast-config/${storeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ configId, userValue })
      });

      if (!response.ok) throw new Error('Failed to update forecasting switch');
      
      setForecastingConfig(prev => prev.map(config => 
        config.configId === configId ? { ...config, userValue } : config
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update switch');
    }
  };

  const getWeekDates = (week: string) => {
    const [year, weekNum] = week.split('-W').map(Number);
    const jan1 = new Date(year, 0, 1);
    const firstMonday = new Date(jan1.getTime() + (1 - jan1.getDay() + 7) % 7 * 86400000);
    const weekStart = new Date(firstMonday.getTime() + (weekNum - 1) * 7 * 86400000);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart.getTime() + i * 86400000);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(amount);
  };

  const groupForecastsByDate = () => {
    const grouped: Record<string, SalesForecast[]> = {};
    forecasts.forEach(forecast => {
      if (!grouped[forecast.date]) {
        grouped[forecast.date] = [];
      }
      grouped[forecast.date].push(forecast);
    });
    return grouped;
  };

  if (loading && !salesHistory.length) {
    return <div className="loading">Loading sales forecasting dashboard...</div>;
  }

  const groupedForecasts = groupForecastsByDate();
  const weekDates = getWeekDates(selectedWeek);

  return (
    <div className="sales-forecasting-dashboard">
      <div className="dashboard-header">
        <h2>AI Sales Forecasting & Shift Optimization</h2>
        <div className="week-selector">
          <label>Week:</label>
          <input
            type="week"
            value={selectedWeek.replace('-W', '-W')}
            onChange={(e) => setSelectedWeek(e.target.value.replace('-W', '-W'))}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="tab-navigation">
        <button 
          className={`tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Sales History
        </button>
        <button 
          className={`tab ${activeTab === 'forecast' ? 'active' : ''}`}
          onClick={() => setActiveTab('forecast')}
        >
          AI Forecasting
        </button>
        <button 
          className={`tab ${activeTab === 'optimization' ? 'active' : ''}`}
          onClick={() => setActiveTab('optimization')}
        >
          Shift Optimization
        </button>
      </div>

      {activeTab === 'history' && (
        <div className="history-section">
          <div className="section-header">
            <h3>Historical Sales Data (Last 30 Days)</h3>
          </div>
          
          <div className="history-summary">
            <div className="summary-card">
              <h4>Total Sales</h4>
              <p>{formatCurrency(salesHistory.reduce((sum, h) => sum + h.sales, 0))}</p>
            </div>
            <div className="summary-card">
              <h4>Total Transactions</h4>
              <p>{salesHistory.reduce((sum, h) => sum + h.transactions, 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h4>Average Ticket</h4>
              <p>{formatCurrency(salesHistory.reduce((sum, h) => sum + h.averageTicket, 0) / salesHistory.length || 0)}</p>
            </div>
          </div>

          <div className="history-chart">
            <h4>Hourly Sales Breakdown</h4>
            <div className="chart-container">
              {Array.from({ length: 24 }, (_, hour) => {
                const hourData = salesHistory.filter(h => h.hour === hour);
                const avgSales = hourData.reduce((sum, h) => sum + h.sales, 0) / hourData.length || 0;
                
                return (
                  <div key={hour} className="hour-bar">
                    <div className="bar-value" style={{ height: `${(avgSales / 1000) * 100}px` }}>
                      {formatCurrency(avgSales)}
                    </div>
                    <div className="bar-label">{hour}:00</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'forecast' && (
        <div className="forecast-section">
          <div className="section-header">
            <h3>AI Sales Forecasting</h3>
            <div className="section-actions">
              <button 
                className="btn btn-primary"
                onClick={generateAIForecast}
                disabled={loading}
              >
                Generate AI Forecast
              </button>
            </div>
          </div>

          <div className="forecasting-switches">
            <h4>Forecasting Adjustments</h4>
            <div className="switches-grid">
              {forecastingConfig.map(config => (
                <div key={config.configId} className="switch-card">
                  <div className="switch-header">
                    <h5>{config.switchName}</h5>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={config.isEnabled}
                        onChange={(e) => updateForecastingSwitch(config.configId, e.target.checked ? config.userValue : 0)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <p className="switch-description">{config.description}</p>
                  {config.isEnabled && (
                    <div className="switch-controls">
                      <label>Impact Weight: {config.userValue}</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={config.userValue}
                        onChange={(e) => updateForecastingSwitch(config.configId, parseFloat(e.target.value))}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="forecast-grid">
            {weekDates.map(date => {
              const dateForecasts = groupedForecasts[date] || [];
              const totalForecast = dateForecasts.reduce((sum, f) => sum + (f.adjustedSales || f.forecastedSales), 0);
              
              return (
                <div key={date} className="forecast-day">
                  <div className="day-header">
                    <h4>{new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</h4>
                    <div className="day-total">{formatCurrency(totalForecast)}</div>
                  </div>
                  
                  <div className="hourly-forecasts">
                    {dateForecasts.map(forecast => (
                      <div key={`${forecast.date}-${forecast.hour}`} className="forecast-hour">
                        <div className="hour-label">{forecast.hour}:00</div>
                        <div className="forecast-values">
                          <div className="ai-forecast">
                            AI: {formatCurrency(forecast.forecastedSales)}
                          </div>
                          <input
                            type="number"
                            value={forecast.adjustedSales}
                            onChange={(e) => updateForecast(forecast.forecastId, parseFloat(e.target.value) || 0)}
                            className="adjusted-forecast"
                            placeholder="Adjust..."
                          />
                          <div className="confidence">
                            {forecast.confidence}% confidence
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'optimization' && (
        <div className="optimization-section">
          <div className="section-header">
            <h3>AI Shift Optimization</h3>
            <div className="section-actions">
              <button 
                className="btn btn-primary"
                onClick={generateOptimization}
                disabled={loading || !forecasts.length}
              >
                Generate Optimization
              </button>
            </div>
          </div>

          <div className="optimization-grid">
            {optimizations.map(opt => (
              <div key={opt.optimizationId} className="optimization-card">
                <div className="card-header">
                  <h4>{new Date(opt.date).toLocaleDateString()}</h4>
                  <span className="shift-type">{opt.shiftType}</span>
                </div>
                
                <div className="staffing-recommendation">
                  <h5>Recommended Staffing</h5>
                  <div className="staff-breakdown">
                    <div className="staff-item">
                      <span>Front of House:</span>
                      <strong>{opt.recommendedStaff.FOH}</strong>
                    </div>
                    <div className="staff-item">
                      <span>Back of House:</span>
                      <strong>{opt.recommendedStaff.BOH}</strong>
                    </div>
                    <div className="staff-item">
                      <span>Management:</span>
                      <strong>{opt.recommendedStaff.MANAGEMENT}</strong>
                    </div>
                  </div>
                </div>

                <div className="optimization-metrics">
                  <div className="metric">
                    <label>Estimated Labor Cost:</label>
                    <span>{formatCurrency(opt.estimatedLabor)}</span>
                  </div>
                  <div className="metric">
                    <label>Labor %:</label>
                    <span>{opt.laborPercentage.toFixed(1)}%</span>
                  </div>
                  <div className="metric">
                    <label>Service Quality Score:</label>
                    <span>{opt.qualityScore}/100</span>
                  </div>
                </div>

                <div className="required-skills">
                  <h5>Required Skills</h5>
                  <div className="skills-tags">
                    {opt.requiredSkills.map(skill => (
                      <span key={skill} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>

                <div className="optimization-actions">
                  <button className="btn btn-secondary">Apply to Schedule</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};